import { useState, useEffect } from 'react'
import { HStack, Text, Spinner, Badge } from '@chakra-ui/react';
import { useFilterCount } from '../../hooks/useContactList'
import { useSavedSearchCount } from '../../hooks/useSavedSearch'
import type { FilterEvaluationRequest, FilterGroup, FilterGroupRequest } from '../../types/contactList'

export type FilterSource = 'lists' | 'saved-searches'

interface LiveCountBadgeProps {
  filterGroups: FilterGroup[]
  source?: FilterSource
}

function toEvaluationRequest(groups: FilterGroup[]): FilterEvaluationRequest | null {
  const nonEmpty = groups.filter(g => g.filters.length > 0 && g.filters.some(f => f.fieldName))
  if (nonEmpty.length === 0) return null
  return {
    filterGroups: nonEmpty.map(g => ({
      groupIndex: g.groupIndex,
      filters: g.filters
        .filter(f => f.fieldName)
        .map(f => ({
          fieldName: f.fieldName,
          operator: f.operator,
          value: f.value,
          displayOrder: f.displayOrder,
        })),
    })) as FilterGroupRequest[],
  }
}

export function LiveCountBadge({ filterGroups, source = 'lists' }: LiveCountBadgeProps) {
  // Debounce by 500ms
  const [debouncedGroups, setDebouncedGroups] = useState(filterGroups)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGroups(filterGroups)
    }, 500)
    return () => clearTimeout(timer)
  }, [filterGroups])

  const request = toEvaluationRequest(debouncedGroups)
  const listResult = useFilterCount(request, source === 'lists' && !!request)
  const savedResult = useSavedSearchCount(request, source === 'saved-searches' && !!request)
  const { data, isLoading, isFetching } = source === 'saved-searches' ? savedResult : listResult

  if (!request) {
    return (
      <Text fontSize="sm" color="gray.400">
        Add filters to see estimate
      </Text>
    )
  }

  return (
    <HStack gap={2}>
      {(isLoading || isFetching) && <Spinner size="sm" color="accent" />}
      {data && (
        <Badge bg="info.subtle" color="info.fg" fontSize="md" px={3} py={1} borderRadius="full">
          {data.count.toLocaleString()} contacts
        </Badge>
      )}
    </HStack>
  );
}

// Export the helper so ListBuilderPage can reuse it for preview
export { toEvaluationRequest }
